/**
 * DepartmentHealthGrid — 21-steward pulse strip.
 *
 * Each tile shows the steward name + a colour-coded status dot
 * (green / yellow / red) + the active/blocked card counts. Click a
 * tile → onOpenDepartment(id). Strict monochrome chrome; the only
 * colour is the status dot.
 */
import { cn } from '../../lib/utils';
import type { DepartmentHealth } from './dashboard-types';

const STATUS_DOT: Record<DepartmentHealth['status'], string> = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  red: 'bg-rose-500',
};

export interface DepartmentHealthGridProps {
  departments: readonly DepartmentHealth[];
  isLoading?: boolean | undefined;
  onOpenDepartment?: ((id: string) => void) | undefined;
  className?: string | undefined;
}

export function DepartmentHealthGrid({
  departments,
  isLoading,
  onOpenDepartment,
  className,
}: DepartmentHealthGridProps) {
  return (
    <section
      aria-labelledby="dept-health-heading"
      className={cn('rounded-md border border-border bg-background p-4', className)}
    >
      <header className="mb-3 flex items-baseline justify-between">
        <h2 id="dept-health-heading" className="text-sm font-semibold text-foreground">
          Department health
        </h2>
        <span className="text-2xs text-muted-foreground">
          {departments.length} steward{departments.length === 1 ? '' : 's'}
        </span>
      </header>
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : departments.length === 0 ? (
        <p className="rounded border border-dashed border-border bg-foreground/[0.02] p-4 text-center text-xs text-muted-foreground">
          No departments configured.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {departments.map((d) => (
            <button
              type="button"
              key={d.id}
              onClick={() => onOpenDepartment?.(d.id)}
              className="group flex flex-col gap-1 rounded border border-border bg-foreground/[0.02] p-3 text-left transition-colors hover:bg-foreground/[0.05]"
            >
              <div className="flex items-center gap-2">
                <span
                  aria-hidden
                  className={cn('h-2 w-2 shrink-0 rounded-full', STATUS_DOT[d.status])}
                />
                <span className="truncate text-xs font-medium text-foreground">{d.name}</span>
              </div>
              <div className="text-2xs text-muted-foreground">
                {d.active_cards} active · {d.blocked_cards} blocked
                {d.sla_breaches_today > 0 && ` · ${d.sla_breaches_today} SLA`}
              </div>
              {d.notes && (
                <div className="truncate text-2xs text-muted-foreground/80" title={d.notes}>
                  {d.notes}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
